import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../contexts/SocketContext';

export const useNotifications = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, token } = useAuth();
  const { 
    notifications, 
    unreadCount, 
    setNotifications, 
    setUnreadCount,
    markNotificationAsRead: socketMarkAsRead,
    markAllAsRead: socketMarkAllAsRead,
    removeNotification: socketRemoveNotification
  } = useSocket();

  // Fetch notifications from server
  const fetchNotifications = async (params = {}) => {
    // Don't fetch if user is not authenticated
    if (!user || !token) {
      console.warn('Cannot fetch notifications: User not authenticated');
      return { notifications: [] };
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/notifications`,
        {
          params,
        }
      );

      // Deduplicate notifications based on ID (in case server returns duplicates)
      const uniqueNotifications = response.data.notifications.reduce((acc, notification) => {
        if (!acc.some(n => n.id === notification.id)) {
          acc.push(notification);
        }
        return acc;
      }, []);

      setNotifications(uniqueNotifications);
      return { ...response.data, notifications: uniqueNotifications };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch notifications';
      console.error('Error fetching notifications:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    // Don't fetch if user is not authenticated
    if (!user || !token) {
      console.warn('Cannot fetch unread count: User not authenticated');
      return 0;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/notifications/unread-count`
      );

      setUnreadCount(response.data.count);
      return response.data.count;
    } catch (err) {
      console.error('Error fetching unread count:', err);
      return 0;
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/notifications/${notificationId}/read`,
        {}
      );

      socketMarkAsRead(notificationId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark notification as read');
      throw err;
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/notifications/read-all`,
        {}
      );

      socketMarkAllAsRead();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark all notifications as read');
      throw err;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/notifications/${notificationId}`
      );

      socketRemoveNotification(notificationId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete notification');
      throw err;
    }
  };

  // Initialize notifications on mount, but only if user is authenticated
  useEffect(() => {
    if (user && token) {
      fetchNotifications({ limit: 50 }).catch(console.error);
      fetchUnreadCount().catch(console.error);
    }
  }, [user, token]); // Depend on user and token

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};

export default useNotifications;
